import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Chip } from "@/components/sync/EndpointCard";

const get = (path, grant, params) => api.get(`/portfolio/real/${path}`, { params, headers: { "X-Portfolio-Grant": grant } }).then((r) => r.data);
const th = "px-2 py-1 text-left font-medium text-slate-500";
const td = "px-2 py-1 border-t border-[var(--border-subtle)]";

function Pager({ d, page, setPage, testId }) {
  const pages = Math.max(1, Math.ceil(d.total / d.page_size));
  return (
    <div className="flex items-center justify-between text-[11px] text-slate-400">
      <span data-testid={`${testId}-total`}>{d.total} record(s)</span>
      <div className="flex items-center gap-2">
        <button data-testid={`${testId}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40 hover:text-white">Prev</button>
        <span>{page} / {pages}</span>
        <button data-testid={`${testId}-next`} disabled={page >= pages} onClick={() => setPage(page + 1)} className="disabled:opacity-40 hover:text-white">Next</button>
      </div>
    </div>
  );
}

function SortTh({ k, label, sort, setSort }) {
  const on = sort.key === k;
  return <th className={th}><button data-testid={`rp-sort-${k}`} onClick={() => setSort({ key: k, desc: on ? !sort.desc : false })} className="hover:text-white">{label}{on ? (sort.desc ? " ↓" : " ↑") : ""}</button></th>;
}

export function HoldingsTable({ grant }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ key: "symbol", desc: false });
  const [page, setPage] = useState(1);
  const { data: d, isError } = useQuery({
    queryKey: ["rp-holdings", grant, q, status, sort, page], gcTime: 0, retry: false,
    queryFn: () => get("holdings", grant, { q, status: status || undefined, sort: sort.key, desc: sort.desc, page, page_size: 25 }),
  });
  if (isError) return <div data-testid="rp-holdings-error" className="text-[11px] text-rose-300">Holdings could not be loaded (unlock may have expired).</div>;
  return (
    <div className="space-y-2" data-testid="rp-holdings">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="rp-holdings-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search symbol" className="h-7 w-40 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
        <select data-testid="rp-holdings-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="h-7 text-xs bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded">
          <option value="">All reconciliation statuses</option>
          {["matched", "missing_in_tradify", "missing_at_broker", "quantity_mismatch", "price_mismatch"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] text-slate-300">
          <thead><tr>{[["symbol", "Symbol"], ["exchange", "Exch"], ["quantity", "Qty"], ["avg_price", "Avg price"], ["recon_status", "Reconciliation"]].map(([k, l]) => <SortTh key={k} k={k} label={l} sort={sort} setSort={(s) => { setSort(s); setPage(1); }} />)}</tr></thead>
          <tbody>
            {d?.rows.map((r) => (
              <tr key={r.symbol} data-testid={`rp-holding-${r.symbol}`}>
                <td className={`${td} font-mono`}>{r.symbol}</td><td className={td}>{r.exchange || "—"}</td>
                <td className={`${td} font-mono`}>{r.quantity}</td><td className={`${td} font-mono`}>{r.avg_price}</td>
                <td className={td}>{r.recon_status ? <Chip status={r.recon_status} /> : "—"}</td>
              </tr>
            ))}
            {d && !d.rows.length && <tr><td colSpan={5} data-testid="rp-holdings-empty" className={`${td} text-slate-500`}>{d.snapshot ? "Sharekhan reports no holdings in the latest snapshot." : "No holdings snapshot yet."}</td></tr>}
          </tbody>
        </table>
      </div>
      {d && <Pager d={d} page={page} setPage={setPage} testId="rp-holdings" />}
      <div className="text-[10px] text-slate-500">Source: latest encrypted Sharekhan snapshot {d?.snapshot?.run_id || "—"} · reconciliation is advisory only.</div>
    </div>
  );
}

export function PositionsTable({ grant }) {
  const { data: d } = useQuery({ queryKey: ["rp-positions", grant], gcTime: 0, retry: false, queryFn: () => get("positions", grant, { page_size: 100 }) });
  return (
    <div data-testid="rp-positions" className="space-y-1 text-[11px] text-slate-400">
      <div className="text-sky-300">UNVERIFIED — shown for reference only; not used in any calculation.</div>
      {d?.rows.length ? d.rows.map((r) => <div key={r.symbol} data-testid={`rp-position-${r.symbol}`} className="font-mono">{r.symbol} · {r.exchange || "—"} · net {r.net_quantity} · {r.product_type || "—"}</div>)
        : <div data-testid="rp-positions-empty">{d?.snapshot ? "No positions reported in the latest snapshot." : "No positions snapshot yet."}</div>}
    </div>
  );
}

export function FundsTable({ grant }) {
  const { data: d } = useQuery({ queryKey: ["rp-funds", grant], gcTime: 0, retry: false, queryFn: () => get("funds", grant) });
  if (!d) return null;
  return (
    <div data-testid="rp-funds" className="space-y-1">
      <div data-testid="rp-funds-note" className="text-[11px] text-sky-300">{d.totals_note}. Values appear only for fields you VERIFIED with evidence (mapping v{d.mapping_version}).</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 text-[11px] text-slate-400">
        {d.rows.map((r) => (
          <div key={r.name} data-testid={`rp-fund-${r.name}`} className="flex justify-between gap-2 border-t border-[var(--border-subtle)] py-0.5">
            <span className="font-mono truncate" title={r.meaning || r.name}>{r.name}</span>
            <span className={r.value_hidden ? "text-slate-500" : "font-mono text-slate-200"}>{r.value_hidden ? `${r.status}${r.present ? "" : " · absent"}` : r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
